import React from "react";
import TablePagination from "../../company/JobManagement/Components/TablePagination";
import { CompanySubscription } from "../model/company_management_model";
import {
  formatCurrency,
  formatDate,
  formatLabel,
  formatNumber,
  getCompanyMeta,
  getCompanyStatusClass,
  getCompanyUserCount,
  getSubscriptionNote,
  getSubscriptionStatusClass,
  getTierBadgeClass,
} from "../view_model/company_management_viewmodel";

interface CompanyManagementTableProps {
  subscriptions: CompanySubscription[];
  total: number;
  page: number;
  pageSize: number;
  submitting: boolean;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
  onViewDetails: (subscription: CompanySubscription) => void;
  onStatusChange: (subscription: CompanySubscription) => void;
}

const renderSubscriptionBadge = (status: string) => (
  <span
    className={`inline-flex w-fit rounded-full px-2.5 py-1 text-xs font-semibold ${getSubscriptionStatusClass(
      status,
    )}`}
  >
    {formatLabel(status)}
  </span>
);

const renderCompanyBadge = (isActive?: boolean | null) => (
  <span
    className={`inline-flex w-fit rounded-full px-2.5 py-1 text-xs font-semibold ${getCompanyStatusClass(
      isActive,
    )}`}
  >
    {isActive === true ? "Active" : isActive === false ? "Inactive" : "Unknown"}
  </span>
);

const CompanyManagementTable: React.FC<CompanyManagementTableProps> = ({
  subscriptions,
  total,
  page,
  pageSize,
  submitting,
  onPageChange,
  onRowsPerPageChange,
  onViewDetails,
  onStatusChange,
}) => (
  <div className="overflow-hidden rounded-lg bg-white shadow">
    <div className="border-b border-gray-200 px-6 py-4">
      <h2 className="text-lg font-semibold text-gray-900">Companies</h2>
    </div>

    <div className="overflow-x-auto">
      <table className="w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Company
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Plan
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Subscription Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Price
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Users
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Balance Credits
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Joined At
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {subscriptions.length === 0 ? (
            <tr>
              <td
                colSpan={9}
                className="px-6 py-10 text-center text-sm text-gray-500"
              >
                No companies matched the current filters.
              </td>
            </tr>
          ) : (
            subscriptions.map((subscription) => {
              const companyMeta = getCompanyMeta(subscription);
              const subscriptionNote = getSubscriptionNote(subscription);
              const shouldActivate = companyMeta?.is_active === false;
              const statusActionLabel = shouldActivate
                ? "Activate"
                : "Deactivate";
              const statusActionClass = shouldActivate
                ? "text-green-600 hover:text-green-900"
                : "text-red-600 hover:text-red-900";

              return (
                <tr key={subscription.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {companyMeta?.name || "Unknown company"}
                      </div>
                      <div className="text-sm text-gray-500">
                        /{companyMeta?.slug || subscription.company_id}
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <span
                        className={`w-fit rounded-full px-2 py-1 text-xs font-medium ${getTierBadgeClass(
                          subscription.tier_name,
                        )}`}
                      >
                        {subscription.tier_name || "Unknown"}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatLabel(subscription.billing_period)}
                      </span>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="flex flex-col gap-1">
                      {renderSubscriptionBadge(subscription.status)}
                      {subscriptionNote ? (
                        <span className="text-xs text-gray-500">
                          {subscriptionNote}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                    <div className="font-semibold">
                      {formatCurrency(subscription.current_period_price)}
                    </div>
                    <div className="text-xs text-gray-500">
                      /{formatLabel(subscription.billing_period).toLowerCase()}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                    {formatNumber(getCompanyUserCount(subscription))}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                    {formatNumber(subscription.current_credit_balance ?? 0)}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                    {formatDate(
                      companyMeta?.created_at ||
                        subscription.started_at ||
                        subscription.created_at,
                    )}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    {renderCompanyBadge(companyMeta?.is_active)}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="text-indigo-600 hover:text-indigo-900"
                        onClick={() => onViewDetails(subscription)}
                      >
                        View
                      </button>
                      <button
                        type="button"
                        className={`${statusActionClass} disabled:cursor-not-allowed disabled:opacity-50`}
                        onClick={() => onStatusChange(subscription)}
                        disabled={submitting}
                      >
                        {submitting ? "Working..." : statusActionLabel}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>

    <TablePagination
      currentPage={page}
      totalCount={total}
      rowsPerPage={pageSize}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
    />
  </div>
);

export default CompanyManagementTable;
